import { Loader, Typography } from '@/shared/ui';
import type { FC } from 'react';

/** The grey shape of one insight card. */
export const InsightCardSkeleton: FC = () => (
  <div className="bg-insight-surface border-insight-border ms-2 flex flex-col gap-3 rounded-l-[10px] border px-[21px] py-4">
    <div className="flex items-center justify-between gap-2">
      <div className="bg-neutral-4 h-4 w-1/2 animate-pulse rounded" />
      <div className="bg-neutral-4 h-[22px] w-16 animate-pulse rounded-xs" />
    </div>
    <div className="bg-neutral-4 h-3 w-full animate-pulse rounded" />
    <div className="bg-neutral-4 h-3 w-full animate-pulse rounded" />
    <div className="bg-neutral-4 h-3 w-2/3 animate-pulse rounded" />
  </div>
);

/**
 * Placeholder until the first line of the insight arrives.
 *
 * Two signals, because the wait is several seconds and each one alone falls
 * short: the shared `Loader` — the same spinner the KPI cards use — says the
 * page is working rather than stuck, while the grey blocks keep the shape of
 * the real content so the panel does not jump when the answer lands.
 *
 * `flex-1` so the placeholder fills the panel, which the panel holds at full
 * height while generating. Anna's review, 2026-09-15.
 */
export const InsightSkeleton: FC = () => (
  <div
    aria-busy="true"
    aria-live="polite"
    data-testid="ai-insight-skeleton"
    className="flex flex-1 flex-col gap-5 pt-4"
  >
    <div
      data-testid="ai-insight-loader"
      className="flex items-center gap-3 px-6"
    >
      <Loader size={20} strokeWidth={6} />
      <Typography className="text-neutral-8">Generating insight…</Typography>
    </div>

    <div className="flex flex-col gap-2 px-6">
      <div className="bg-neutral-4 h-4 w-full animate-pulse rounded" />
      <div className="bg-neutral-4 h-4 w-4/5 animate-pulse rounded" />
    </div>

    <InsightCardSkeleton />
    <InsightCardSkeleton />
  </div>
);

/**
 * Trails the cards already streamed in, while the model writes the next one —
 * so a panel showing one card does not read as finished.
 */
export const InsightNextCardSkeleton: FC = () => (
  <div aria-busy="true" data-testid="ai-insight-next-card">
    <InsightCardSkeleton />
  </div>
);
